import classnames from 'classnames';
import React, { useState } from 'react';
import moment from 'moment';
import { FiCalendar } from '@vyaguta/vyaguta-icons/fi';
import { UiArrowLeft, UiArrowRight } from '@vyaguta/vyaguta-icons/ui';
import { SingleValue } from 'react-select';

import 'react-dates/lib/css/_datepicker.css';

import { SingleDatePicker } from 'react-dates';
import { ICON_AFTER_POSITION } from 'react-dates/constants';

import { MONTH_MAPPING } from 'constants/date';

import { getCalendarMonthsOption, getCalendarYearsOption, getWeek } from 'utils/date';

import DatePickerDropdown from './DatePickerDropdown';

type Props = {
  fromEnhancedFilter?: boolean;
  isDark?: boolean;
  pastYearsCount?: number;
  futureYearsCount?: number;
  label?: React.ReactNode;
} & Omit<
  React.ComponentProps<typeof SingleDatePicker>,
  'focused' | 'onFocusChange' | 'renderMonthElement' | 'id' | 'placeholder'
>;

type DatePickerDropdownOption = {
  label: string | number;
  value: number;
};

type MonthElementProps = {
  month: moment.Moment;
  onMonthSelect: (currentMonth: moment.Moment, monthNumber: number) => void;
  onYearSelect: (currentMonth: moment.Moment, yearNumber: number) => void;
};

const SmallDatePicker: React.FC<Props> = (props) => {
  const { fromEnhancedFilter, isDark, ...rest } = props;
  const [focused, setFocused] = useState(false);

  const renderMonthElement = ({ month, onMonthSelect, onYearSelect }: MonthElementProps) => {
    const handleMonthSelect = (
      currentMonth: moment.Moment,
      monthSelectHandler: (monthValue: moment.Moment, monthNumber: number) => void,
      event: SingleValue<DatePickerDropdownOption>
    ) => {
      if (!event) return;
      return monthSelectHandler(currentMonth, event.value);
    };

    const handleYearSelect = (
      currentMonth: moment.Moment,
      yearSelectHandler: (monthValue: moment.Moment, yearNumber: number) => void,
      event: SingleValue<DatePickerDropdownOption>
    ) => {
      if (!event) return;
      return yearSelectHandler(currentMonth, event.value);
    };

    const monthLabelMap = MONTH_MAPPING as Record<number, string>;
    const monthValue = { label: monthLabelMap[month.month()], value: month.month() };

    const yearValue = { label: month.year(), value: month.year() };

    const onMonthChange = (event: SingleValue<DatePickerDropdownOption>) =>
      handleMonthSelect(month, onMonthSelect, event);

    const onYearChange = (event: SingleValue<DatePickerDropdownOption>) => handleYearSelect(month, onYearSelect, event);

    const monthDropdown = DatePickerDropdown(getCalendarMonthsOption(), monthValue, onMonthChange);
    const yearDropdown = DatePickerDropdown(getCalendarYearsOption(), yearValue, onYearChange);
    const weekHeaderItems = getWeek().map((item) => (
      <p key={item} className="title">
        {item}
      </p>
    ));

    return (
      <div>
        <div className="d-flex justify-content-center align-items-start gap-px-8">
          {monthDropdown}
          {yearDropdown}
        </div>

        <div className="d-flex date-picker-week-header">{weekHeaderItems}</div>
      </div>
    );
  };

  return (
    <div
      className={classnames('small-date__wrapper', {
        'enhanced-filter__date-picker': fromEnhancedFilter,
        'enhanced-filter__date-picker--dark': isDark,
      })}
    >
      <div className="small-date__label">{props.label}</div>
      <SingleDatePicker
        id="date"
        isOutsideRange={() => false}
        placeholder="Select Date"
        focused={focused}
        displayFormat="LL"
        numberOfMonths={1}
        customInputIcon={<FiCalendar size="18px" color="#DADADA" />}
        navPrev={<UiArrowLeft size={20} className="color-grey-80" />}
        navNext={<UiArrowRight size={20} className="color-grey-80" />}
        onFocusChange={({ focused: nextFocused }) => setFocused(Boolean(nextFocused))}
        inputIconPosition={ICON_AFTER_POSITION}
        hideKeyboardShortcutsPanel={true}
        renderMonthElement={renderMonthElement}
        {...rest}
      />
    </div>
  );
};

export default SmallDatePicker;
